import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useOrg } from "@/context/OrgContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChartPieSlice,
  Factory,
  Lightning,
  Graph,
  Table as TableIcon,
  FileText,
  SignOut,
  Buildings,
  Leaf,
  Target as TargetIcon,
  UsersThree,
  UploadSimple,
  Ruler,
  ShieldCheck,
  CaretUpDown,
  MapPinArea,
  Queue,
  ClipboardText,
  Scales,
  Coins,
  ClipboardText as ClipboardIcon,
  UsersFour,
  BookOpen,
  Globe,
  Code,
  ChartLineUp,
} from "@phosphor-icons/react";

const NavItem = ({ to, icon: Icon, label, testid, badge }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 ${
        isActive
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
      }`
    }
  >
    <Icon size={18} weight="duotone" />
    <span className="flex-1">{label}</span>
    {badge != null && badge > 0 && (
      <span className="text-[10px] font-mono-data bg-accent text-accent-foreground px-1.5 py-0.5 rounded" data-testid={`${testid}-badge`}>{badge}</span>
    )}
  </NavLink>
);

export default function AppShell() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const { orgs, activeOrgId, switchOrg, role } = useOrg();
  const nav = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (role !== "owner") { setPendingCount(0); return; }
    let cancelled = false;
    const load = () => api.get("/approvals/count")
      .then((r) => { if (!cancelled) setPendingCount(r.data.pending || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 15000);
    const handler = () => load();
    window.addEventListener("cl:pending-changed", handler);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("cl:pending-changed", handler); };
  }, [role, activeOrgId]);

  const doLogout = async () => {
    await logout();
    localStorage.removeItem("cl_active_org");
    nav("/login");
  };

  const handleSwitch = (v) => {
    if (v === "__new__") {
      nav("/app/organizations");
      return;
    }
    switchOrg(v);
    // reload current view
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex bg-background grain-bg relative z-0">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/60 backdrop-blur-sm sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-border">
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Leaf size={20} weight="fill" />
            </div>
            <div>
              <div className="font-display font-semibold text-base leading-tight">{t("appName")}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-widest">Sustainability Coaching · ISO 14064</div>
            </div>
          </Link>
        </div>

        {/* Org switcher */}
        {orgs.length > 0 && (
          <div className="px-3 pt-3">
            <Select value={activeOrgId} onValueChange={handleSwitch}>
              <SelectTrigger className="w-full h-auto py-2 text-left" data-testid="org-switcher">
                <SelectValue>
                  <div className="flex items-center gap-2 min-w-0">
                    <Buildings size={16} weight="duotone" className="text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{orgs.find((o) => o.id === activeOrgId)?.name || "Select organization"}</div>
                      <div className="text-[10px] uppercase tracking-widest font-mono-data text-muted-foreground">{role || "—"}</div>
                    </div>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {orgs.map((o) => (
                  <SelectItem key={o.id} value={o.id} data-testid={`org-option-${o.id}`}>
                    <div className="flex flex-col">
                      <span>{o.name}</span>
                      <span className="text-[10px] uppercase font-mono-data text-muted-foreground">{o.role} · {o.reporting_year}</span>
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value="__new__" data-testid="org-new">+ New organization…</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItem to="/app" icon={ChartPieSlice} label={t("dashboard")} testid="nav-dashboard" />
          <NavItem to="/app/trends" icon={ChartLineUp} label={t("trendsNav")} testid="nav-trends" />
          <NavItem to="/app/scope1" icon={Factory} label={t("scope1")} testid="nav-scope1" />
          <NavItem to="/app/scope2" icon={Lightning} label={t("scope2")} testid="nav-scope2" />
          <NavItem to="/app/scope3" icon={Graph} label={t("scope3")} testid="nav-scope3" />
          <NavItem to="/app/factors" icon={TableIcon} label={t("factors")} testid="nav-factors" />
          <NavItem to="/app/facilities" icon={MapPinArea} label={t("facilities")} testid="nav-facilities" />
          <NavItem to="/app/targets" icon={TargetIcon} label={t("targets")} testid="nav-targets" />
          <NavItem to="/app/intensity" icon={Ruler} label={t("intensity")} testid="nav-intensity" />
          <NavItem to="/app/verification" icon={ShieldCheck} label={t("verification")} testid="nav-verification" />
          <NavItem to="/app/cbam" icon={Scales} label={t("cbam")} testid="nav-cbam" />
          <NavItem to="/app/carbon-tax" icon={Coins} label={t("carbonTax")} testid="nav-carbon-tax" />
          <NavItem to="/app/esrs" icon={ClipboardIcon} label={t("esrs")} testid="nav-esrs" />
          <NavItem to="/app/gri" icon={Globe} label={t("gri")} testid="nav-gri" />
          <NavItem to="/app/clients" icon={UsersFour} label={t("clientsNav")} testid="nav-clients" />
          <NavItem to="/app/glossary" icon={BookOpen} label={t("glossaryNav")} testid="nav-glossary" />
          <NavItem to="/app/api" icon={Code} label={t("apiDocsNav")} testid="nav-api" />
          <NavItem to="/app/import" icon={UploadSimple} label={t("import")} testid="nav-import" />
          <NavItem to="/app/jobs" icon={Queue} label={t("jobs")} testid="nav-jobs" />
          {role === "owner" && (
            <NavItem to="/app/approvals" icon={ClipboardText} label={t("approvals")} testid="nav-approvals" badge={pendingCount} />
          )}
          <NavItem to="/app/team" icon={UsersThree} label={t("team")} testid="nav-team" />
          <NavItem to="/app/report" icon={FileText} label={t("report")} testid="nav-report" />
          <NavItem to="/app/organizations" icon={Buildings} label={t("organizations")} testid="nav-orgs" />
          <NavItem to="/app/organization" icon={Buildings} label={t("settings")} testid="nav-org" />
        </nav>
        <div className="p-3 border-t border-border">
          <div className="text-xs text-muted-foreground truncate mb-2">{user?.email}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={doLogout}
            data-testid="logout-btn"
          >
            <SignOut size={16} className="mr-2" />
            {t("logout")}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-10 flex items-center justify-between px-6">
          <div className="text-sm text-muted-foreground">{t("tagline")}</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
              <button
                data-testid="lang-tr"
                onClick={() => setLang("tr")}
                className={`px-3 py-1.5 font-mono-data ${lang === "tr" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
              >
                TR
              </button>
              <button
                data-testid="lang-en"
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 font-mono-data ${lang === "en" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
              >
                EN
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 px-6 py-6 relative z-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
